import React from "react";
import { Link } from "react-router-dom";
import PublicLayout from "../shell/PublicLayout";
import Overline from "../components/Overline";
import StatusChip from "../components/StatusChip";
import useLiveStatus from "../api/useLiveStatus";
import { paths } from "../routing/paths";

/*
 * /live — the Castr stream in the redesign: breadcrumbs, live/offline
 * status header (webhook-driven, polled), the player in a navy card, and
 * a "while you wait" row when nothing is on air.
 */

const CASTR_PLAYER = "https://player.castr.com/live_92ed788083e411ea9ddeb156daa60b82";

const STATES = {
  live: {
    chip: { kind: "live", label: "LIVE NOW" },
    blurb: "Rabbi Paltiel is teaching right now — press play to join the class.",
  },
  soon: {
    chip: { kind: "soon", label: "STARTING SOON" },
    blurb: "A class is about to begin. The stream starts automatically once Rabbi Paltiel goes on air.",
  },
  offair: {
    chip: { kind: "countdown", label: "OFFLINE" },
    blurb: "The stream is offline right now. This page updates on its own when Rabbi Paltiel goes live.",
  },
};

const WHILE_YOU_WAIT = [
  { href: paths.dailyTanya(), kicker: "Tanya", title: "Today's shiur" },
  { href: paths.dailyHayomYom(), kicker: "Hayom Yom", title: "Today's entry" },
  { href: paths.latestClasses(), kicker: "Archive", title: "Latest classes" },
];

export default function LivePage() {
  const status = useLiveStatus();
  const state = STATES[status?.state] || STATES.offair;

  return (
    <PublicLayout>
      <main className="max-w-[1280px] w-full mx-auto px-5 pt-5 pb-10 flex flex-col gap-5">
        <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-[14px] leading-5 whitespace-nowrap">
          <Link to={paths.home()} className="text-ic-blue font-semibold py-3 px-1 -my-3 inline-flex items-center min-h-[44px] hover:underline">
            Home
          </Link>
          <span className="text-ic-slate">→</span>
          <span className="text-ic-body font-semibold px-1">Live</span>
        </nav>

        <section className="flex flex-col gap-2">
          <div className="flex items-center gap-3 flex-wrap">
            <Overline>Live</Overline>
            {status && (
              <StatusChip kind={state.chip.kind} role="status" aria-live="polite">
                {state.chip.label}
              </StatusChip>
            )}
          </div>
          <h1 className="m-0 font-extrabold text-black tracking-[-0.5px]" style={{ fontSize: "clamp(26px, 6vw, 40px)", lineHeight: 1.2 }}>
            Watch Rabbi Paltiel live
          </h1>
          <p className="m-0 text-[18px] font-normal leading-7 text-ic-body max-w-[720px]">
            {status?.state === "soon" && status.label ? status.label : state.blurb}
          </p>
        </section>

        <section className="bg-ic-navy border border-white/10 rounded-xl shadow-ic-raised-dark overflow-hidden max-w-[960px] w-full">
          <iframe
            src={CASTR_PLAYER}
            title="Inside Chassidus live stream"
            className="block w-full bg-black"
            style={{ aspectRatio: "16 / 9", minHeight: "220px" }}
            frameBorder="0"
            scrolling="no"
            allow="autoplay; fullscreen"
            allowFullScreen
          />
        </section>

        {status?.state !== "live" && (
          <section className="flex flex-col gap-3">
            <Overline>While you wait</Overline>
            <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))" }}>
              {WHILE_YOU_WAIT.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className="bg-white border border-ic-lightGray rounded-xl p-3 min-h-[72px] flex flex-col justify-center gap-0.5 no-underline hover:no-underline shadow-ic-raised transition-all duration-150 ease-out hover:shadow-ic-elevated hover:border-ic-borderGray focus-visible:outline-none focus-visible:border-ic-blue focus-visible:shadow-ic-focus-soft"
                >
                  <span className="text-[12px] font-bold leading-4 tracking-[0.5px] uppercase text-ic-blue">{item.kicker}</span>
                  <span className="text-[16px] font-semibold leading-6 text-black">{item.title}</span>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
    </PublicLayout>
  );
}
